<script>
	import * as Yup from "yup";
	import Forms, { useValidations } from "../../ursCommon/Forms.svelte";
	import { FormGroup, Input } from "@sveltestrap/sveltestrap";
	import MemberIdInput from "../../common/MemberIdInput.svelte";
	import { maskA1CValue } from "../../common/Forms.svelte";
	import { dateStuff } from "../../common/Date.svelte";
	import * as Constants from "../../common/Constants.js";

	const { validateField, validateForm, getErrors, resetErrors, removeError } = useValidations();
	const {	toLocalMidnight, getISODateOnly, addDays, addMonths, addYears, parseDateOnly, isValidISODate, normalizeDate, } = dateStuff();

	const today = new Date();
	const minDate = new Date(
		today.getFullYear() - 150,
		today.getMonth(),
		today.getDate(),
	);
	const a1cRegex = /^(?!00%$)(?!00\.0%$)(?!0\.0%$)[0-7]?[0-9](?:\.[0-9])?%?$/;
	const minCurrentA1cDate = addMonths(getISODateOnly(toLocalMidnight(new Date())), -3);
	const maxDate = toLocalMidnight(new Date());
	const minBirthdate = getISODateOnly(minDate);
	const maxBirthdate = getISODateOnly(maxDate);

	let {
		disabled = true,
	} = $props();

	const formSchema = Yup.object({
		firstName: Yup.string()
			.matches(Constants.NAME_REGEX, Constants.NAME_MATCH_SUGGESTION)
			.required("Please enter the patient's first name."),
		lastName: Yup.string()
			.matches(Constants.NAME_REGEX, Constants.NAME_MATCH_SUGGESTION)
			.required("Please enter the patient's last name."),
		weight: Yup.string()
			.required("Please enter the patient's weight.")
			.matches(/^[1-9]\d{1,3}(\.\d{1,2})?$/, {
				message: "Invalid weight.",
				excludeEmptyString: true,
			}),
		birthDate: Yup.date()
			.transform((value, originalVal) => (originalVal === "" ? null : value))
			.nullable()
			.required("Please enter a valid date.")
			.max(new Date(), "Cannot be in the future.")
			.min(minDate, "Birthdate is out of range."),
		baselineA1c: Yup.string()
			.required("Please enter baseline A1C.")
			.matches(a1cRegex, "Invalid A1C."),
		baselineDateTaken: Yup.date()		
			.transform((value, originalVal) => (originalVal === "" ? null : value))
			.nullable()
			.required("Please enter a valid date.")
			.max(toLocalMidnight(new Date()), "Invalid date: A1C test date cannot be in the future.")
			.test('baseline-a1c-date-limits',
				"",
				function () {
					const rawBirthDate = this.parent?.birthDate;
					const bd = normalizeDate(rawBirthDate);
					const rawBaselineDate = this.parent?.baselineDateTaken;
					const baseLineDt = normalizeDate(rawBaselineDate);
					const rawCurrentA1cDate = this.parent?.currentDateTaken;
					const currentA1cDt = normalizeDate(rawCurrentA1cDate);

					if (!bd || !baseLineDt) return true;

					//baseline date must be on or after birthdate
					if (baseLineDt < bd) {
						return this.createError({
							path: this.path,
							message: 'Invalid date: A1C test date must be after birthdate.',
						});
					}

					//baseline date must be before current a1c date
					if (!baseLineDt || !currentA1cDt) return true;
					if (baseLineDt >= currentA1cDt) {
						return this.createError({
							path: this.path,
							message: 'Invalid date: Baseline A1C must precede the most recent A1C date.',
						});
					}
					return true;
				}
			),
		currentA1c: Yup.string()
			.required("Please enter current A1C")
			.matches(a1cRegex, "Invalid A1C"),
		currentDateTaken: Yup.date()	
			.transform((value, originalVal) => (originalVal === "" ? null : value))
			.nullable()	
			.required("Please enter a valid date.")
			.max(toLocalMidnight(new Date()), "Invalid date: A1C cannot be in the future.")
			.test('current-a1c-date-limits', '', function () {
				const today = normalizeDate(maxDate);
				const minCurrentA1cDt = normalizeDate(minCurrentA1cDate);
				const rawBirthDate = this.parent?.birthDate;
				const bd = normalizeDate(rawBirthDate);
				const rawCurrentA1cDate = this.parent?.currentDateTaken;
				const currentA1cDt = normalizeDate(rawCurrentA1cDate);
				const rawBaseLineA1cDate = this.parent?.baselineDateTaken;
				const baselineA1cDt = normalizeDate(rawBaseLineA1cDate);

				//current A1c date cannot be older than 3 months
				if (!minCurrentA1cDt || !currentA1cDt) return true;
				else {
					if (minCurrentA1cDt > currentA1cDt) {
						return this.createError({
							path: this.path,
							message: 'Invalid date: A test result from the last 3 months is required.',
						});
					}
				}

				//current A1c Date must be on or after birthdate
				if (!bd || !currentA1cDt) return true;
				else {
						if (currentA1cDt < bd) {
						return this.createError({
							path: this.path,
							message: 'Invalid date: A1C test date must be after birthdate.',
						});
					}
				}

				//current A1C Date must be after the baseline date
				if (!baselineA1cDt || !currentA1cDt) return true;
				else {
					if (baselineA1cDt >= currentA1cDt) {
						return this.createError({
							path: this.path,
							message: 'Invalid date: Recent A1C must occur after the baseline A1C date.',
						});
					}
				}
				return true;
			}),
	});

	let memberIdInput;

	let errors = $derived(getErrors());
	let formHasErrors = $state(false);
	let formData = $state({
		firstName: "",
		lastName: "",
		birthDate: "",
		weight: "",
		memberId: "",
		baselineA1c: "",
		baselineDateTaken: "",
		currentA1c: "",
		currentDateTaken: "",
	});

	let minBaselineDate = $state(minBirthdate);

	export function clearAllErrors() {
		resetErrors();
	}

	export function getValues() {
		return formData;
	}

	export function setValues(data) {
		formData.firstName = data.firstName;
		formData.lastName = data.lastName;
		formData.birthDate = data.birthDate;
		formData.weight = data.weight;
		formData.memberId = memberIdInput.getValues()?.memberId;
		resetErrors();
	}

	export function hasErrors() {
		return formHasErrors;
	}

	export async function validate() {
		memberIdInput.validate();
		formData.memberId = memberIdInput.getValues().memberId;
		await validateForm(formSchema, formData);
	}

	function handleChange(fieldName, fieldValue) {
		const vars = new Map();
		vars.set(fieldName, fieldValue);
		const data = Object.fromEntries(vars);
		for (const key in data) {
			validateField(formSchema, key, formData);
		}
	}

	function handleBirthDateChange(fieldName, fieldValue){
		handleChange(fieldName, fieldValue);
		minBaselineDate = fieldValue;
		if (formData.baselineDateTaken !== "") validateField(formSchema, "baselineDateTaken", formData);
		if (formData.currentDateTaken !== "" ) validateField(formSchema, "currentDateTaken", formData);
	}
	function handleBaseLineDateChange(fieldName, fieldValue){
		handleChange(fieldName, fieldValue);
		if (formData.birthDate !== "") validateField(formSchema, "birthDate", formData);
		if (formData.currentDateTaken !== "" ) validateField(formSchema, "currentDateTaken", formData);
	}
	function handleCurrentDateChange(fieldName, fieldValue){
		handleChange(fieldName, fieldValue);
		if (formData.baselineDateTaken !== "") validateField(formSchema, "baselineDateTaken", formData);
		if (formData.birthDate !== "" ) validateField(formSchema, "birthDate", formData);
	}

	function formatDate(date) {
		const year = date.getFullYear();
		const month = (date.getMonth() + 1).toString().padStart(2, "0");
		const day = date.getDate().toString().padStart(2, "0");
		return `${year}-${month}-${day}`;
	}

	
	function handleBaselineA1cInput(event) {
		const input = event.target;
		formData.baselineA1c = maskA1CValue(
			input,
			event.inputType,
			"II.F%",
			formData.baselineA1c,
		);
		validateField(formSchema, "baselineA1c", formData);
	}	
	function handleCurrentA1cInput(event) {
		const input = event.target;
		formData.currentA1c = maskA1CValue(
			input,
			event.inputType,
			"II.F%",
			formData.currentA1c,
		);
		validateField(formSchema, "currentA1c", formData);
	}
</script>

<main>
	<div class="row">
		<div class="col-xs-12 col-sm-12 col-md-4">
			<FormGroup floating label="Patient First Name">
				<Input
					id="firstName"
					name="firstName"
					bind:value={formData.firstName}
					onchange={(e) => handleChange("firstName", e.target.value)}
					invalid={Boolean(errors.firstName)}
					feedback={errors.firstName}
					type="text"
					disabled={disabled}
				/>
			</FormGroup>
		</div>
		<div class="col-xs-12 col-sm-12 col-md-4">
			<FormGroup floating label="Patient Last Name">
				<Input
					id="lastName"
					name="lastName"
					bind:value={formData.lastName}
					onchange={(e) => handleChange("lastName", e.target.value)}
					invalid={Boolean(errors.lastName)}
					feedback={errors.lastName}
					type="text"
					disabled={disabled}
				/>
			</FormGroup>
		</div>
		<div class="col-xs-12 col-sm-12 col-md-4">
			<FormGroup floating label="Patient Date of Birth">
				<Input
					type="date"
					bind:value={formData.birthDate}
					invalid={Boolean(errors.birthDate)}
					feedback={errors.birthDate}
					on:blur={(e) => handleBirthDateChange("birthDate", e.target.value)}
					min={minBirthdate}
					max={maxBirthdate}
					disabled={disabled}
				/>
			</FormGroup>
		</div>
	</div>
	<div class="row">
		<div class="col-xs-12 col-sm-12 col-md-4">
			<MemberIdInput 
				bind:this={memberIdInput}
				disabled={disabled}
			/>
		</div>
		<div class="col-xs-12 col-sm-12 col-md-4">
			<FormGroup floating label="Patient Weight (lbs)">
				<Input
					id="patientWeight"
					name="patientWeight"
					bind:value={formData.weight}
					onchange={(e) => handleChange("weight", e.target.value)}
					invalid={Boolean(errors.weight)}
					feedback={errors.weight}
					inputmode="numeric"
					maxlength="7"
					disabled={disabled}
				/>
			</FormGroup>
		</div>
	</div>
	<div class="row">
	<div class="col-sm-3">
		<FormGroup floating spacing="mb-3" label="Baseline A1C">
			<Input
				type="text"
				bind:value={formData.baselineA1c}
				oninput={handleBaselineA1cInput}
				invalid={Boolean(errors.baselineA1c)}
				feedback={errors.baselineA1c}
				inputmode="numeric"
				disabled={disabled}
			/>
	</FormGroup>
	</div>
	<div class="col-sm-3">
		<FormGroup floating spacing="mb-3" label="Baseline A1C Date Taken">
			<Input
				type="date"
				bind:value={formData.baselineDateTaken}
				invalid={Boolean(errors.baselineDateTaken)}
				feedback={errors.baselineDateTaken}
				on:blur={(e) => handleBaseLineDateChange("baselineDateTaken", e.target.value)}
				disabled={disabled}
				min={minBaselineDate}
				max={getISODateOnly(maxDate)}
			/>
		</FormGroup>
	</div>
	<div class="col-sm-3">
		<FormGroup floating spacing="mb-3" label="Current A1C">
			<Input
				type="text"
				bind:value={formData.currentA1c}
				oninput={handleCurrentA1cInput}
				invalid={Boolean(errors.currentA1c)}
				feedback={errors.currentA1c}
				inputmode="numeric"
				disabled={disabled}
			/>
	</FormGroup>
	</div>
	<div class="col-sm-3">
		<FormGroup floating spacing="mb-3" label="Current A1C Date Taken">
			<Input
				type="date"
				bind:value={formData.currentDateTaken}
				invalid={Boolean(errors.currentDateTaken)}
				feedback={errors.currentDateTaken}
				on:blur={(e) => handleCurrentDateChange("currentDateTaken", e.target.value)}
				disabled={disabled}
				min={minCurrentA1cDate}
				max={getISODateOnly(maxDate)}
			/>
		</FormGroup>
	</div>
</div>
</main>
